// Where a join request through the open invitation link stands, from what the
// signed-in person may read: their pendingInvitations/{uid} request, their
// restaurants/{id}/users/{uid} membership (roles) and their users/{uid} profile.
export type RequestStatus = 'ready' | 'approved' | 'pending' | 'declined' | 'missing';

export const requestStatusFor = ({
  membership,
  profileHasRestaurant,
  request,
}: {
  membership: boolean;
  profileHasRestaurant: boolean;
  request: { status?: string } | null | undefined;
}): RequestStatus => {
  // The dashboard needs both: roles from the membership, the restaurant list from the profile.
  if (membership && profileHasRestaurant) return 'ready';
  if (request?.status === 'success') return 'approved';
  if (request?.status === 'pending') return 'pending';
  // The admin's "decline" stores status 'failed'.
  if (request?.status === 'failed') return 'declined';
  return 'missing';
};

export type InvitationProblem =
  | 'invalid-link'
  | 'wrong-credentials'
  | 'account-exists'
  | 'weak-password'
  | 'network'
  | 'popup-closed'
  | 'too-many-requests'
  | 'unknown';

const PROBLEM_BY_CODE: Record<string, InvitationProblem> = {
  'auth/wrong-password': 'wrong-credentials',
  'auth/user-not-found': 'wrong-credentials',
  'auth/invalid-credential': 'wrong-credentials',
  'auth/invalid-login-credentials': 'wrong-credentials',
  'auth/invalid-email': 'wrong-credentials',
  'auth/email-already-in-use': 'account-exists',
  'auth/weak-password': 'weak-password',
  'auth/network-request-failed': 'network',
  unavailable: 'network',
  'auth/popup-closed-by-user': 'popup-closed',
  'auth/cancelled-popup-request': 'popup-closed',
  'auth/too-many-requests': 'too-many-requests',
};

export const invitationProblemFor = (error: unknown): InvitationProblem => {
  const code = (error as { code?: unknown } | undefined)?.code;
  return (typeof code === 'string' && PROBLEM_BY_CODE[code]) || 'unknown';
};

// English keys of the helpersAndErrors namespace.
export const PROBLEM_TEXT: Record<InvitationProblem, string> = {
  'invalid-link': 'This invitation link is not valid. Please ask the restaurant for a current link.',
  'wrong-credentials': 'The email address or password is not correct.',
  'account-exists':
    'There already is a Gastronaut account for this email address. Choose "I already have an account" and sign in.',
  'weak-password': 'This password is not secure enough. Please choose a longer one.',
  network: 'The connection failed. Please try again.',
  'popup-closed': 'The Google window was closed before the sign-in finished.',
  'too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  unknown: 'Something went wrong. Please try again.',
};
